import { useEffect, useRef, useState } from 'react'
import type { UseFormReturn, FieldValues, Path } from 'react-hook-form'
import { appStore } from '@/store'

/**
 * Collects the dot-paths of every field that already holds a value. Empty strings and
 * `false` booleans count as unanswered, so a brand new application yields nothing.
 */
function filledPaths(value: unknown, prefix = ''): string[] {
	if (value === null || value === undefined) return []
	if (typeof value === 'object' && !Array.isArray(value)) {
		return Object.entries(value as Record<string, unknown>).flatMap(([key, child]) =>
			filledPaths(child, prefix ? `${prefix}.${key}` : key)
		)
	}
	if (typeof value === 'string') return value.trim() === '' ? [] : [prefix]
	if (typeof value === 'boolean') return value ? [prefix] : []
	return [prefix]
}

/**
 * Hook to trigger validation on subsequent visits to a form.
 * - On first visit, only validates fields that already carry saved data, so returning
 *   applicants see stale answers flagged (e.g. a birth date that is now out of range)
 *   without new applicants facing a wall of "required" errors.
 * - On subsequent visits, triggers validation on mount so errors show.
 * - Tracks per-form visit state in localStorage.
 *
 * Usage:
 *   useFormVisitValidation('general', formMethods)
 */
export function useFormVisitValidation<T extends FieldValues>(
	formKey: string,
	form: UseFormReturn<T>
) {
	const hasRun = useRef(false)
	const [visitedBefore, setVisitedBefore] = useState(false)
	useEffect(() => {
		if (hasRun.current) return
		hasRun.current = true
		// If the application is already submitted, skip triggering validation on mount
		try {
			const current = appStore.snapshot().user
			if (current?.application_process === 'Submitted') return
		} catch {
			// ignore
		}

		let u = null as unknown as { _id?: string } | null
		try {
			u = appStore.snapshot().user ?? null
		} catch {
			u = null
		}
		if (!u || !u._id) return

		const visitKey = `formVisited:${formKey}:${String(u._id)}`
		let visited: string | null = null
		try {
			visited = localStorage.getItem(visitKey)
		} catch {
			visited = null
		}
		if (visited) {
			setVisitedBefore(true)
			void form.trigger()
		} else {
			try {
				localStorage.setItem(visitKey, '1')
			} catch {
				// ignore storage failures
			}
			const prefilled = filledPaths(form.getValues())
			if (prefilled.length > 0) void form.trigger(prefilled as Path<T>[])
		}
	}, [formKey, form])

	return visitedBefore
}
